import { useEffect, useState } from "react";
import { Download, ListChecks, ArrowRight, Loader2, GripVertical, ShieldAlert, ShieldCheck, Ban } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const load = async () => {
    try {
      const [pRes, lRes] = await Promise.all([
        api.get("/crm/pipeline"),
        api.get("/leads", { params: { q: q || undefined } }) // backend doesn't filter by pipeline_stage yet in GET /leads, so we'll filter on frontend
      ]);
      setPipeline(pRes.data);
      let loadedLeads = lRes.data;
      
      // Frontend filter by pipeline stage
      if (stageFilter !== "all") {
        loadedLeads = loadedLeads.filter(l => l.pipeline_stage === stageFilter);
      }
      
      setLeads(loadedLeads);
      // Clear selection on reload
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      toast.error("Failed to load prospects");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stageFilter]);

  const exportCsv = async () => {
    const token = localStorage.getItem("lf_token");
    const res = await fetch(`${API}/leads/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `prospects.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleToggleSelect = (id) => {
    const newSel = new Set(selectedIds);
    if (newSel.has(id)) newSel.delete(id);
    else newSel.add(id);
    setSelectedIds(newSel);
  };

  const handleToggleAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const bulkDnd = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Mark ${selectedIds.size} leads as Do Not Disturb?`)) return;
    setBulkActionLoading(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => api.patch(`/leads/${id}/dnd`, { dnd: true })));
      toast.success(`Marked ${selectedIds.size} leads as DND`);
      load();
    } catch (e) {
      toast.error("Bulk DND update failed");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkUpdateStage = async (stageId) => {
    if (selectedIds.size === 0) return;
    setBulkActionLoading(true);
    try {
      await api.post("/crm/leads/bulk-stage", {
        lead_ids: Array.from(selectedIds),
        pipeline_stage: stageId
      });
      toast.success(`Moved ${selectedIds.size} leads to new stage`);
      load();
    } catch (e) {
      toast.error("Bulk update failed");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkVerify = async () => {
    if (selectedIds.size === 0) return;
    setBulkActionLoading(true);
    try {
      const r = await api.post("/leads/bulk-verify", {
        lead_ids: Array.from(selectedIds)
      });
      const total = r.data.total;
      const results = r.data.results || {};
      const valid = Object.values(results).filter(s => s === "valid").length;
      const invalid = Object.values(results).filter(s => s === "invalid").length;
      const catchAll = Object.values(results).filter(s => s === "catch_all").length;
      toast.success(`Verified ${total}: ${valid} deliverable, ${invalid} undeliverable, ${catchAll} catch-all`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk verify failed");
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto pb-24 relative min-h-screen">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">?" Prospects</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">All prospects</h1>
        </div>
        <Button onClick={exportCsv} variant="outline" className="rounded-lg border-[#EAEAE3] hover:bg-[#F4F4EE]" data-testid="leads-export-csv">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Input placeholder="Search by name, company, email..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-sm rounded-lg bg-white shadow-sm" data-testid="leads-search" />
        
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-56 rounded-lg bg-white shadow-sm" data-testid="leads-stage-filter">
            <SelectValue placeholder="Filter by Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pipeline Stages</SelectItem>
            {pipeline?.stages?.map(s => (
              <SelectItem key={s.id} value={s.id}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={load} className="rounded-lg bg-[#0A0A0F] text-white hover:bg-[#14141B]" data-testid="leads-search-btn">Search</Button>
      </div>

      <LeadTable 
        leads={leads} 
        pipeline={pipeline}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleAll={handleToggleAll}
        onRefresh={load} 
      />

      {/* Floating Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0A0A0F] text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 animate-in slide-in-from-bottom-10 z-50">
          <div className="flex items-center gap-2 font-mono text-sm">
            <div className="w-6 h-6 rounded-md bg-[#1E4FD6] text-white flex items-center justify-center font-bold">
              {selectedIds.size}
            </div>
            <span>selected</span>
          </div>
          
          <div className="w-px h-8 bg-white/20" />
          
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white/70">Move to stage:</span>
            <Select onValueChange={bulkUpdateStage} disabled={bulkActionLoading}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white hover:bg-white/20 transition-colors">
                {bulkActionLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/> Moving...</> : <SelectValue placeholder="Select Stage..." />}
              </SelectTrigger>
              <SelectContent>
                {pipeline?.stages?.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-px h-8 bg-white/20" />

          <Button
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10"
            onClick={bulkVerify}
            disabled={bulkActionLoading}
          >
            <ShieldCheck className="h-4 w-4 mr-1.5" /> Verify emails
          </Button>

          <Button
            variant="ghost"
            className="text-white/70 hover:text-red-400 hover:bg-white/10"
            onClick={bulkDnd}
            disabled={bulkActionLoading}
          >
            <Ban className="h-4 w-4 mr-1.5" /> Mark DND
          </Button>
          
          <div className="w-px h-8 bg-white/20" />
          
          <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => setSelectedIds(new Set())}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
